<script setup lang="ts">
  import { computed, onMounted, onUnmounted } from 'vue';
  import type { TableCell, TableElement } from '../../../model/form.types';
  import { useFormBuilderStore } from '../../../model/form-builder.store';
  import { useTableEditorStore } from '../../../model/table-editor.store';
  import { useFormRulesStore } from '../../../model/form-rules.store';
  import { useFormPermissionsSelectionStore } from '../../../model/form-permissions-selection.store';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import type { FormBuilderInteractionMode } from '../../../lib/interaction-mode';
  import { isRulesSelectableType } from '../../../lib/rules-field-eligibility';
  import TableGrid from '../table/TableGrid.vue';

  interface Props {
    element: TableElement;
    interactionMode?: FormBuilderInteractionMode;
    showAnchorHighlights?: boolean;
    showReportHighlights?: boolean;
    locked?: boolean;
  }

  const props = defineProps<Props>();

  const formBuilderStore = useFormBuilderStore();
  const tableEditorStore = useTableEditorStore();
  const formRulesStore = useFormRulesStore();
  const permissionsSelectionStore = useFormPermissionsSelectionStore();

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'TABLE'
  );

  const isRulesMode = computed(() => props.interactionMode === 'rules');
  const isViewMode = computed(() => props.interactionMode === 'view');
  const isPermissionsMode = computed(() => props.interactionMode === 'permissions');
  const canSelectRuleTargets = computed(() => !props.locked);
  const isActive = computed(
    () => !isPermissionsMode.value && tableEditorStore.activeTableId === props.element.id
  );
  const isSelected = computed(() => {
    if (isPermissionsMode.value) return false;

    return isRulesMode.value
      ? canSelectRuleTargets.value && formRulesStore.activeTableId === props.element.id
      : formBuilderStore.selectedElementId === props.element.id;
  });

  const selectedCell = computed(() => {
    if (isRulesMode.value || !isActive.value) return null;
    return tableEditorStore.selectedCell;
  });

  const selectedCells = computed(() => {
    if (isPermissionsMode.value) {
      return props.element.properties.cells.flatMap((row: TableCell[]) =>
        row
          .filter(
            (cell) =>
              cell.content &&
              isRulesSelectableType(cell.content.type) &&
              permissionsSelectionStore.isFieldSelected(cell.content.id, props.element.id)
          )
          .map((cell) => ({ row: cell.rowIndex, col: cell.colIndex }))
      );
    }
    if (isRulesMode.value) {
      if (!canSelectRuleTargets.value) return [];
      return formRulesStore.getSelectedCellsForTable(props.element.id);
    }
    if (!isActive.value) return [];
    return [...tableEditorStore.selectedCells];
  });

  const rulesMenuCell = computed(() => {
    if (
      !isRulesMode.value ||
      formRulesStore.ruleItemSelectionActive ||
      formRulesStore.menuAnchor.kind !== 'table-cell'
    ) {
      return null;
    }

    if (formRulesStore.menuAnchor.tableId !== props.element.id) {
      return null;
    }

    return {
      row: formRulesStore.menuAnchor.row,
      col: formRulesStore.menuAnchor.col,
    };
  });

  const rulesMenuScope = computed(() => {
    if (
      !isRulesMode.value ||
      formRulesStore.ruleItemSelectionActive ||
      formRulesStore.menuAnchor.kind !== 'table-scope'
    ) {
      return null;
    }

    if (formRulesStore.menuAnchor.tableId !== props.element.id) {
      return null;
    }

    return {
      scope: formRulesStore.menuAnchor.scope,
      index: formRulesStore.menuAnchor.index,
    };
  });

  const rulesMenuCellContent = computed(() => {
    const cell = rulesMenuCell.value;
    if (!cell) return null;

    return props.element.properties.cells[cell.row]?.[cell.col]?.content ?? null;
  });

  const showRulesAddRule = computed(
    () =>
      isRulesMode.value &&
      formRulesStore.canAddRule &&
      formRulesStore.canAddRuleForElement(rulesMenuCellContent.value)
  );

  const anchorCell = computed(() => {
    if (isRulesMode.value || !isActive.value) return null;
    return tableEditorStore.anchorCell;
  });

  const activeCell = computed(() => {
    if (isRulesMode.value || !isActive.value) return null;
    return tableEditorStore.activeCell;
  });

  const handleTableClick = () => {
    if (isViewMode.value || props.locked) return;

    if (isPermissionsMode.value) {
      if (!permissionsSelectionStore.addMode) permissionsSelectionStore.dismissFill();
      return;
    }

    if (isRulesMode.value) {
      if (!canSelectRuleTargets.value) return;
      formRulesStore.activateTable(props.element.id);
      tableEditorStore.exitEditingMode();
      return;
    }

    if (!isActive.value) {
      formBuilderStore.selectElement(props.element.id);
      tableEditorStore.enterEditingMode(props.element.id);
    }
  };

  const handleCellSelect = (
    row: number,
    col: number,
    additive: boolean = false,
    shiftKey: boolean = false
  ) => {
    if (isViewMode.value || isRulesMode.value || isPermissionsMode.value || props.locked) {
      return;
    }

    if (formBuilderStore.selectedElementId !== props.element.id) {
      formBuilderStore.selectElement(props.element.id);
    }

    if (!isActive.value) {
      tableEditorStore.enterEditingMode(props.element.id);
    }

    tableEditorStore.selectCell(row, col, additive, shiftKey);
  };

  const handleCellRangeSelect = (
    startRow: number,
    startCol: number,
    endRow: number,
    endCol: number
  ) => {
    if (isActive.value) {
      tableEditorStore.selectCellRange(startRow, startCol, endRow, endCol);
    }
  };

  const handleRowSelect = (rowIndex: number) => {
    if (isActive.value) {
      tableEditorStore.selectRow(rowIndex);
    }
  };

  const handleColumnSelect = (colIndex: number) => {
    if (isActive.value) {
      tableEditorStore.selectColumn(colIndex);
    }
  };

  const handleSelectAll = () => {
    if (isActive.value) {
      tableEditorStore.selectAllCells();
    }
  };

  const handlePermissionsSelect = (row: number, col: number, event: MouseEvent | KeyboardEvent) => {
    if (props.locked) return;

    const fieldId = props.element.properties.cells[row]?.[col]?.content?.id;

    // The browser echoes a label click as a second click on its input, which would undo a pick.
    if ((event.target as HTMLElement | null)?.closest('label')) event.preventDefault();

    if (!permissionsSelectionStore.addMode) {
      permissionsSelectionStore.dismissFill();
      if (fieldId) permissionsSelectionStore.pickField(fieldId);
      else permissionsSelectionStore.clearPick();
      return;
    }

    if (!fieldId) return;

    permissionsSelectionStore.selectField([...formBuilderStore.elements], fieldId, {
      additive: event.metaKey || event.ctrlKey,
      range: event.shiftKey,
    });
  };

  const handleRulesFieldSelect = (
    row: number,
    col: number,
    event: MouseEvent | KeyboardEvent
  ) => {
    if (isPermissionsMode.value) {
      handlePermissionsSelect(row, col, event);
      return;
    }

    if (!isRulesMode.value || !canSelectRuleTargets.value) return;

    const additive = event.metaKey || event.ctrlKey || event.shiftKey;
    formRulesStore.selectTableField(props.element, row, col, additive);
  };

  const handleRulesRowScope = (rowIndex: number, event?: MouseEvent) => {
    if (props.locked) return;

    if (isPermissionsMode.value) {
      permissionsSelectionStore.selectRow(props.element, Number(rowIndex), {
        additive: !!event && (event.metaKey || event.ctrlKey),
      });
      return;
    }

    if (!isRulesMode.value || !canSelectRuleTargets.value) return;
    formRulesStore.selectTableScope(props.element, 'row', Number(rowIndex));
  };

  const handleRulesColumnScope = (colIndex: number) => {
    if (!isRulesMode.value || !canSelectRuleTargets.value) return;
    formRulesStore.selectTableScope(props.element, 'column', colIndex);
  };

  const handleRulesTableScope = (event?: MouseEvent) => {
    if (props.locked) return;

    if (isPermissionsMode.value) {
      permissionsSelectionStore.selectTable(props.element, {
        additive: !!event && (event.metaKey || event.ctrlKey),
      });
      return;
    }

    if (!isRulesMode.value || !canSelectRuleTargets.value) return;
    formRulesStore.selectTableScope(props.element, 'table');
  };

  const handleAddRow = () => {
    if (isActive.value) {
      tableEditorStore.addRow();
    }
  };

  const handleAddColumn = () => {
    if (isActive.value) {
      tableEditorStore.addColumn();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isActive.value) return;

    if (event.key === 'Escape') {
      if (tableEditorStore.selectedCells.length > 0) {
        tableEditorStore.clearCellSelection();
      } else {
        tableEditorStore.exitEditingMode();
      }
    }
  };

  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
  });
</script>

<template>
  <div
    ref="tableElementRef"
    class="table-element"
    :class="{ 'editing-mode': isActive }"
    :style="containerStyles"
    @click.stop="handleTableClick"
  >
    <TableGrid
      :table-element="element"
      :is-editable="isActive"
      :is-selected="isSelected"
      :selected-cell="selectedCell"
      :selected-cells="selectedCells"
      :anchor-cell="anchorCell"
      :active-cell="activeCell"
      :interaction-mode="props.interactionMode ?? 'builder'"
      :show-anchor-highlights="props.showAnchorHighlights"
      :show-report-highlights="props.showReportHighlights"
      :rules-menu-cell="rulesMenuCell"
      :rules-menu-scope="rulesMenuScope"
      :show-rules-add-rule="showRulesAddRule"
      @select-cell="handleCellSelect"
      @select-cell-range="handleCellRangeSelect"
      @select-row="handleRowSelect"
      @select-column="handleColumnSelect"
      @select-all="handleSelectAll"
      @select-content="handleRulesFieldSelect"
      @scope-row="handleRulesRowScope"
      @scope-column="handleRulesColumnScope"
      @scope-table="handleRulesTableScope"
      @add-row="handleAddRow"
      @add-column="handleAddColumn"
    />
  </div>
</template>

<style scoped>
  .table-element {
    position: relative;
    font-family: var(--font-base);
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
</style>
